import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, ClipboardList, HandCoins, Landmark, Clock, Car, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { StatusBadge, TableShell, EmptyRow } from "../components/Shared";

const Card = ({ icon: Icon, label, value, accent, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Icon size={18} className={accent} strokeWidth={2} />
    </div>
    <div className="font-heading text-2xl md:text-3xl font-bold text-slate-900">{value}</div>
  </div>
);

export default function Dashboard() {
  const { user, t, fmt } = useApp();
  const [data, setData] = useState(null);
  const [todayOrders, setTodayOrders] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    const today = new Date().toISOString().slice(0, 10);
    api.get("/schedule", { params: { date: today } }).then((r) => setTodayOrders(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400">…</div>;
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">{t("dashboard")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("welcome")}, {user?.name}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin && (
          <>
            <Card testId="card-month-income" icon={TrendingUp} label={t("monthIncome")} value={fmt(data.month_income)} accent="text-emerald-600" />
            <Card testId="card-month-expense" icon={TrendingDown} label={t("monthExpenses")} value={fmt(data.month_expense)} accent="text-red-500" />
          </>
        )}
        <Card testId="card-open-orders" icon={ClipboardList} label={t("openOrders")} value={data.open_orders} accent="text-sky-600" />
        {isAdmin ? (
          <Card testId="card-receivables" icon={HandCoins} label={t("orderBalance")} value={fmt(data.order_receivables)} accent="text-orange-500" />
        ) : (
          <Card testId="card-completed" icon={Landmark} label={t("completedOrders")} value={data.completed_orders} accent="text-emerald-600" />
        )}
      </div>
      <div data-testid="today-schedule-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarDays size={20} className="text-sky-600" /> {t("todaySchedule")}
          </h2>
          <Link to="/schedule" data-testid="view-schedule-link" className="text-sm text-sky-600 hover:text-sky-800 font-medium">
            {t("schedule")} →
          </Link>
        </div>
        {todayOrders.length === 0 ? (
          <div data-testid="today-schedule-empty" className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-400 text-sm">
            {t("noAppointments")}
          </div>
        ) : (
          <div className="space-y-2">
            {todayOrders.map((o) => (
              <div key={o.id} data-testid={`today-schedule-item-${o.order_number}`}
                className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-sm flex flex-wrap items-center gap-x-6 gap-y-1">
                <div className="flex items-center gap-1.5 font-heading font-bold text-sky-600 w-16">
                  <Clock size={15} /> {(o.scheduled_at || "").slice(11, 16) || "--:--"}
                </div>
                <div className="min-w-[160px]">
                  <span className="font-semibold text-slate-900">{o.order_number}</span>
                  <span className="text-slate-600"> — {o.customer_name}</span>
                </div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 uppercase">
                  <Car size={13} /> {o.vehicle} {o.plate && `· ${o.plate}`}
                </div>
                {o.adas_type && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                    o.adas_status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    ADAS
                  </span>
                )}
                <div className="ml-auto"><StatusBadge status={o.status} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{t("recentOrders")}</h2>
          <Link to="/work-orders" data-testid="view-all-orders-link" className="text-sm text-sky-600 hover:text-sky-800 font-medium">
            {t("workOrders")} →
          </Link>
        </div>
        <TableShell headers={[t("order"), t("customer"), t("vehicle"), t("total"), t("status")]}>
          {data.recent_orders.length === 0 && <EmptyRow colSpan={5} />}
          {data.recent_orders.map((o) => (
            <tr key={o.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold text-slate-900">{o.order_number}</td>
              <td className="px-4 py-3">{o.customer_name}</td>
              <td className="px-4 py-3 text-slate-500">{o.vehicle} {o.plate && `· ${o.plate}`}</td>
              <td className="px-4 py-3 font-medium">{fmt(o.total)}</td>
              <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
            </tr>
          ))}
        </TableShell>
      </div>
    </div>
  );
}
